/**
 * Balance Summary Component
 * Displays policy balance information in a table format
 */

import { type BalanceResponse } from '@/types/api.types';
import { formatCurrency } from '@/utils/formatters';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/common';
import { cn } from '@/lib/utils';

interface BalanceSummaryProps {
  balance: BalanceResponse;
}

export default function BalanceSummary({ balance }: BalanceSummaryProps) {
  return (
    <div className="space-y-4">
      {/* Policy Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Policy Information</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Policy Details */}
            <div className="space-y-3">
              <div>
                <span className="text-sm text-text-muted">Policy Number</span>
                <p className="text-sm font-medium text-text">{balance.policyNumber}</p>
              </div>
              <div>
                <span className="text-sm text-text-muted">Payment Type</span>
                <p className="mt-1">
                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                      balance.isAuditPremium
                        ? 'bg-success/10 text-success'
                        : 'bg-primary/10 text-primary'
                    )}
                  >
                    {balance.isAuditPremium ? 'Audit Premium' : 'Deductible'}
                  </span>
                </p>
              </div>
            </div>

            {/* Right Column - Insured Address */}
            <div>
              <span className="text-sm text-text-muted">Insured Address</span>
              <div className="mt-1">
                <p className="text-sm font-medium text-text">{balance.insuredName}</p>
                <p className="text-sm text-text">{balance.insuredAddress}</p>
                <p className="text-sm text-text">{balance.insuredCityStateZip}</p>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Balance Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Balance Summary</CardTitle>
        </CardHeader>
        <CardBody className="!p-0">
          {balance.isAuditPremium ? (
            /* Audit Premium Balance Display */
            <table className="w-full">
              <thead className="sr-only">
                <tr>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-4 py-3 text-sm text-text">Past Due Amount</td>
                  <td className="px-4 py-3 text-sm text-text text-right font-medium">
                    {formatCurrency(balance.pastDue)}
                  </td>
                </tr>
                <tr className="border-b border-border bg-surface/50">
                  <td className="px-4 py-3 text-sm text-text">Fees Due Amount</td>
                  <td className="px-4 py-3 text-sm text-text text-right font-medium">
                    {formatCurrency(balance.feesAmount ?? 0)}
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-3 text-sm text-text">Payments/Adjustments</td>
                  <td className="px-4 py-3 text-sm text-text text-right font-medium">
                    {formatCurrency(balance.paidAmount ?? 0)}
                  </td>
                </tr>
                <tr className="bg-surface">
                  <td className="px-4 py-4 text-base font-semibold text-text">Total Balance Due</td>
                  <td className="px-4 py-4 text-base font-bold text-text text-right">
                    {formatCurrency(balance.totalBalance)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            /* Deductible Balance Display */
            <table className="w-full">
              <thead className="sr-only">
                <tr>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-4 py-3 text-sm text-text">Current Amount Due</td>
                  <td className="px-4 py-3 text-sm text-text text-right font-medium">
                    {formatCurrency(balance.currentDue)}
                  </td>
                </tr>
                {balance.pastDue > 0 && (
                  <tr className="border-b border-border bg-danger/5">
                    <td className="px-4 py-3 text-sm text-danger font-medium">Past Due Amount</td>
                    <td className="px-4 py-3 text-sm text-danger text-right font-medium">
                      {formatCurrency(balance.pastDue)}
                    </td>
                  </tr>
                )}
                <tr className="bg-surface">
                  <td className="px-4 py-4 text-base font-semibold text-text">Total Balance Due</td>
                  <td className="px-4 py-4 text-base font-bold text-text text-right">
                    {formatCurrency(balance.totalBalance)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* RSUI Contact Information - Only show when there's a balance due */}
      {balance.totalBalance > 0 && balance.rsuiContact && balance.rsuiContact.name && (
        <Card variant="default" className="bg-primary/5 border-l-4 border-l-primary">
          <CardBody>
            <p className="text-sm text-text">
              If you have a question about your bill, please contact:{' '}
              <span className="font-semibold">{balance.rsuiContact.name}</span>
              {balance.rsuiContact.phone && (
                <>
                  {' '}
                  at{' '}
                  <a
                    href={`tel:${balance.rsuiContact.phone}`}
                    className="text-primary hover:underline"
                  >
                    {balance.rsuiContact.phone}
                  </a>
                </>
              )}
              {balance.rsuiContact.email && (
                <>
                  {' '}
                  or{' '}
                  <a
                    href={`mailto:${balance.rsuiContact.email}`}
                    className="text-primary hover:underline"
                  >
                    {balance.rsuiContact.email}
                  </a>
                </>
              )}
              .
            </p>
          </CardBody>
        </Card>
      )}

      {/* Important Notice - Only for Deductible with past due balance */}
      {!balance.isAuditPremium && balance.pastDue > 0 && (
        <Card variant="error">
          <CardBody>
            <div className="flex items-start gap-3">
              <svg
                className="h-5 w-5 text-danger flex-shrink-0 mt-0.5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-text">
                You have a past due balance of{' '}
                <span className="font-semibold">{formatCurrency(balance.pastDue)}</span>. Please
                send the payment as soon as possible.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
